import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function TechnicalTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-10 py-6 -mx-10 -mt-10 mb-8" style={{ textAlign: s.headerAlignment }}>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="font-bold tracking-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${1.875 * hScale}rem` }}>{personal.fullName || 'Your Name'}</h1>
            <p className="text-slate-300 mt-2 text-sm">{personal.email}</p>
          </div>
          <div className="text-right text-sm text-slate-300 space-y-1">
            {personal.phone && <p>{personal.phone}</p>}
            {personal.location && <p>{personal.location}</p>}
            {personal.linkedin && <p>{personal.linkedin}</p>}
            {personal.website && <p>{personal.website}</p>}
          </div>
        </div>
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {skills.length > 0 && (
          <section className="bg-slate-50 -mx-10 px-10 py-6">
            <h2 className="font-bold text-slate-900 mb-4 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Technical Skills</h2>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <span key={skill.id} className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-slate-700 text-white" style={{ fontSize: `${0.75 * bScale}rem` }}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Professional Experience</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <div key={exp.id} className="bg-slate-50 rounded-lg p-5">
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{exp.title}</h3>
                    <span className="font-medium text-slate-500 bg-white px-3 py-1 rounded-full border border-slate-200" style={{ fontSize: `${0.875 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-700 font-semibold mt-1" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.company}{exp.location && ` • ${exp.location}`}</p>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </div>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Technical Projects</h2>
            <div className="grid grid-cols-2 gap-4">
              {projects.map((proj) => (
                <div key={proj.id} className="border border-slate-200 rounded-lg p-4">
                  <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{proj.name}</h3>
                  <p className="text-slate-600 mt-1" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {proj.technologies.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded border border-slate-200 font-medium" style={{ fontSize: `${0.75 * bScale}rem` }}>{t}</span>)}
                    </div>
                  )}
                  {proj.url && <p className="text-slate-500 mt-2" style={{ fontSize: `${0.75 * bScale}rem` }}>{proj.url}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Education</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <div key={edu.id} className="flex justify-between items-baseline">
                  <div>
                    <h3 className="font-bold text-slate-900" style={{ fontSize: `${1 * hScale}rem` }}>{edu.school}</h3>
                    <p className="text-slate-700" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                  </div>
                  <span className="text-slate-500" style={{ fontSize: `${0.875 * bScale}rem` }}>
                    {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold text-slate-900 mb-4 pb-2 border-b-2 border-slate-800 uppercase tracking-wide" style={{ fontSize: `${1.125 * hScale}rem` }}>Certifications</h2>
            <div className="flex flex-wrap gap-2">
              {certifications.map((cert) => (
                <div key={cert.id} className="border-2 border-slate-800 px-4 py-2 rounded-lg">
                  <span className="font-bold text-slate-900" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.name}</span>
                  <span className="text-slate-500 ml-2" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer}{cert.date && ` • ${cert.date}`}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
